import React, { useMemo, useState } from 'react';
import { sitesApi } from './sitesApi';

const LOOK_LABELS = {
  'bold-block': 'Bold Block, high contrast, condensed type',
  editorial: 'Editorial, classic rhythm, serif display',
  bento: 'Bento, modular grid, crisp blocks',
  'soft-modern': 'Soft Modern, calm curves, warm neutrals',
  'swiss-minimal': 'Swiss Minimal, tight grid, quiet type',
};

const ALL_LOOKS = ['bold-block', 'editorial', 'bento', 'soft-modern', 'swiss-minimal'];

/** Labels aligned with packages/site-design/src/data/typePairings.json */
const TYPE_PAIRINGS = [
  { id: 'modern', label: 'Archivo + Inter' },
  { id: 'geometric', label: 'Outfit + Inter' },
  { id: 'humanist', label: 'Source Sans 3' },
  { id: 'serif-display', label: 'Fraunces + Inter' },
  { id: 'condensed', label: 'Barlow Condensed + Inter' },
  { id: 'system-classic', label: 'System classic' },
];

/** Swatches from packages/site-design fallback palettes (brand color only). */
const PALETTE_SWATCHES = [
  { id: 'trust-1', brand: '#1e4d7b' },
  { id: 'trust-2', brand: '#0f766e' },
  { id: 'trust-3', brand: '#1d4ed8' },
  { id: 'craft-1', brand: '#78350f' },
  { id: 'craft-2', brand: '#44403c' },
  { id: 'response-1', brand: '#b45309' },
];

function buildGallery(ranking, shuffleSeed) {
  const merged = [];
  for (const id of [...(ranking || []), ...ALL_LOOKS]) {
    if (!merged.includes(id) && merged.length < 6) merged.push(id);
  }
  while (merged.length < 6) merged.push(ALL_LOOKS[merged.length % ALL_LOOKS.length]);
  return merged.slice(0, 6).map((look, i) => {
    const pairing = TYPE_PAIRINGS[i % TYPE_PAIRINGS.length];
    const palette = PALETTE_SWATCHES[i % PALETTE_SWATCHES.length];
    return {
      look,
      variant: `${shuffleSeed}-${i}-${look}`,
      typePairingId: pairing.id,
      paletteId: palette.id,
      label: LOOK_LABELS[look] || look,
    };
  });
}

export default function DesignGallery({ workspaceId, siteId, ranking, brief, selectedLook, onPickLook, onPreviewUrl }) {
  const [shuffleSeed, setShuffleSeed] = useState('gallery');
  const [thumbUrls, setThumbUrls] = useState({});
  const [fineOpen, setFineOpen] = useState(false);
  const [density, setDensity] = useState('comfortable');
  const [roundness, setRoundness] = useState('soft');
  const [contrast, setContrast] = useState('standard');
  const [typePairingId, setTypePairingId] = useState('modern');
  const [paletteId, setPaletteId] = useState('trust-1');

  const cards = useMemo(() => buildGallery(ranking, shuffleSeed), [ranking, shuffleSeed]);

  React.useEffect(() => {
    let cancelled = false;
    async function loadThumbs() {
      if (!workspaceId || !siteId) return;
      await Promise.all(
        cards.map(async (c) => {
          if (cancelled) return;
          try {
            const res = await sitesApi.renderSite(workspaceId, siteId, {
              look: c.look,
              variant: c.variant,
              typePairingId: c.typePairingId,
              paletteId: c.paletteId,
            });
            if (cancelled || !res.previewUrl) return;
            setThumbUrls((prev) => ({ ...prev, [c.variant]: res.previewUrl }));
          } catch {
            /* next card */
          }
        })
      );
    }
    setThumbUrls({});
    loadThumbs();
    return () => { cancelled = true; };
  }, [cards, workspaceId, siteId]);

  async function persistDesignPrefs(overrides = {}) {
    if (!brief) return;
    const designPrefs = {
      typePairingId,
      paletteId,
      density,
      roundness,
      contrast,
      ...overrides,
    };
    const nextBrief = { ...brief, designPrefs: { ...(brief.designPrefs || {}), ...designPrefs } };
    await sitesApi.updateSite(workspaceId, siteId, { brief: nextBrief });
  }

  async function handlePick(card) {
    onPickLook(card.look);
    setTypePairingId(card.typePairingId);
    setPaletteId(card.paletteId);
    const designPrefs = {
      typePairingId: card.typePairingId,
      paletteId: card.paletteId,
      density,
      roundness,
      contrast,
    };
    const patch = { look: card.look };
    if (brief) {
      patch.brief = {
        ...brief,
        designPrefs: { ...(brief.designPrefs || {}), ...designPrefs },
      };
    }
    await sitesApi.updateSite(workspaceId, siteId, patch);
    const res = await sitesApi.renderSite(workspaceId, siteId, {
      look: card.look,
      typePairingId: card.typePairingId,
      paletteId: card.paletteId,
    });
    if (res.previewUrl) onPreviewUrl(res.previewUrl);
  }

  function handleShuffle() {
    setThumbUrls({});
    setShuffleSeed(`s-${Date.now()}`);
  }

  async function handleDialChange(field, value) {
    if (field === 'density') setDensity(value);
    if (field === 'roundness') setRoundness(value);
    if (field === 'contrast') setContrast(value);
    await persistDesignPrefs({ [field]: value });
  }

  return (
    <div style={{ padding: '0 0.25rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
        <p style={{ margin: 0, fontWeight: 600 }}>Pick a design</p>
        <button
          type="button"
          onClick={handleShuffle}
          style={{ padding: '0.4rem 0.85rem', borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer' }}
        >
          Shuffle
        </button>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: '0.65rem',
          marginBottom: '1rem',
        }}
      >
        {cards.map((card) => (
          <button
            key={card.variant}
            type="button"
            className="gallery-card design-card"
            data-variant-key={card.variant}
            onClick={() => handlePick(card)}
            style={{
              border: selectedLook === card.look ? '2px solid #171A0D' : '1px solid #e5e7eb',
              borderRadius: 10,
              padding: 0,
              overflow: 'hidden',
              cursor: 'pointer',
              background: '#fff',
              textAlign: 'left',
            }}
          >
            <div style={{ aspectRatio: '3/4', maxHeight: 132, background: '#f3f4f6', position: 'relative', overflow: 'hidden' }}>
              {thumbUrls[card.variant] && (
                <iframe
                  title={card.look}
                  src={thumbUrls[card.variant]}
                  style={{ width: '200%', height: '200%', transform: 'scale(0.5)', transformOrigin: 'top left', border: 'none', pointerEvents: 'none' }}
                />
              )}
            </div>
            <div style={{ padding: '0.5rem', fontSize: '0.75rem', color: '#374151', lineHeight: 1.3 }}>{card.label}</div>
          </button>
        ))}
      </div>
      <button
        type="button"
        onClick={() => setFineOpen((o) => !o)}
        style={{ background: 'none', border: 'none', color: '#4b5563', textDecoration: 'underline', cursor: 'pointer', padding: 0 }}
      >
        Fine-tune
      </button>
      {fineOpen && (
        <div style={{ marginTop: '0.75rem', padding: '0.75rem', border: '1px solid #e5e7eb', borderRadius: 8 }}>
          <p style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Type pairing</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>
            {TYPE_PAIRINGS.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={async () => {
                  setTypePairingId(p.id);
                  await persistDesignPrefs({ typePairingId: p.id });
                }}
                style={{
                  padding: '0.35rem 0.75rem',
                  borderRadius: 6,
                  border: typePairingId === p.id ? '2px solid #171A0D' : '1px solid #e5e7eb',
                  background: '#fff',
                  fontSize: '0.8rem',
                }}
              >
                {p.label}
              </button>
            ))}
          </div>
          <p style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Palette</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>
            {PALETTE_SWATCHES.map((p) => (
              <button
                key={p.id}
                type="button"
                title={p.id}
                aria-label={p.id}
                onClick={async () => {
                  setPaletteId(p.id);
                  await persistDesignPrefs({ paletteId: p.id });
                }}
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 8,
                  border: paletteId === p.id ? '2px solid #171A0D' : '1px solid #e5e7eb',
                  background: p.brand,
                  cursor: 'pointer',
                }}
              />
            ))}
          </div>
          <p style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Density</p>
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
            {['compact', 'comfortable'].map((d) => (
              <button key={d} type="button" onClick={() => handleDialChange('density', d)} style={{ padding: '0.35rem 0.75rem', borderRadius: 6, border: density === d ? '2px solid #171A0D' : '1px solid #e5e7eb', background: '#fff' }}>
                {d}
              </button>
            ))}
          </div>
          <p style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Roundness</p>
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
            {['sharp', 'soft'].map((d) => (
              <button key={d} type="button" onClick={() => handleDialChange('roundness', d)} style={{ padding: '0.35rem 0.75rem', borderRadius: 6, border: roundness === d ? '2px solid #171A0D' : '1px solid #e5e7eb', background: '#fff' }}>
                {d}
              </button>
            ))}
          </div>
          <p style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Contrast</p>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {['standard', 'high'].map((d) => (
              <button key={d} type="button" onClick={() => handleDialChange('contrast', d)} style={{ padding: '0.35rem 0.75rem', borderRadius: 6, border: contrast === d ? '2px solid #171A0D' : '1px solid #e5e7eb', background: '#fff' }}>
                {d}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
